import React from 'react';
import { useSelector } from 'react-redux';

import { APPROACHING_CONTENT_LIMIT_BANNER_DISMISSED } from 'constants/events';
import {
  EXP_INVITE_STORAGE_INCENTIVES,
  ROLLOUT_CONTINUE_WATCHING,
} from 'constants/featureFlag';
import { PERMISSION_GRANTED } from 'constants/notifications';
import {
  REQUEST_PUSH_PERMISSIONS,
  SHOW_DATA_RETENTION_WARNING,
  SHOW_MEMBER_VIDEO_THRESHOLD_BANNER,
  SHOW_MOBILE_BANNER_PROMPT_ON_SHARE_PAGE,
} from 'constants/triggers';
import { selectUserIsOwner } from 'selectors/shareVideo';
import { selectUserIsLogged } from 'selectors/user';
import { useApproachingContentLimitBannerState } from 'utilities/localStorage/storageIncentives';

import useMedia from 'hooks/useMedia';
import { useShowContinueWatchingBanner } from 'hooks/banners';
import { useFlagIsActivated } from 'hooks/featureFlag';
import { useShouldShowTrigger, useUrlTriggerReset } from 'hooks/triggers';
import {
  useCheckIfSdkSharedUser,
  useGetMemberVideoLimits,
} from 'hooks/workspace';

import * as analytics from '../../../utilities/analytics';
import MemberVideoThresholdBanner from './MemberVideoThresholdBanner';

import MobileDownloadBanner from 'components/account-settings/mobile-download-banner';
import { SharePageDataRetentionBanner } from 'components/share-video/data-retention-banner';
import ApproachingLimitBanner from 'components/storage-incentives/approaching-limit-banner';
import WebPermissionsBanner from 'components/web-permissions-banners/share-page';

const Banners = ({ video }) => {
  const isLoggedIn = useSelector(selectUserIsLogged);
  const shouldRequestPushPermissions = useShouldShowTrigger(
    REQUEST_PUSH_PERMISSIONS
  );
  const hasRecordedFirstVideo = useSelector(
    state => state.checklist.first_video_recording
  );

  const isMobile = useMedia(['(max-width: 767px)'], [true], false);

  const isVideoOwner = useSelector(selectUserIsOwner);

  const [canShowApproachingLimitBanner, setShowApproachingLimitBanner] =
    useApproachingContentLimitBannerState();

  const hasStorageIncentive = useFlagIsActivated({
    flag: EXP_INVITE_STORAGE_INCENTIVES,
    activationValues: ['variant'],
  });

  const shouldShowMobileBanner = useShouldShowTrigger(
    SHOW_MOBILE_BANNER_PROMPT_ON_SHARE_PAGE
  );

  const shouldShowDataRetentionBanner =
    useShouldShowTrigger(SHOW_DATA_RETENTION_WARNING) && isVideoOwner;

  const browserSupport = 'Notification' in window;

  const memberVideoLimit = useGetMemberVideoLimits();
  const showMemberVideoThresholdBanner = useShouldShowTrigger(
    SHOW_MEMBER_VIDEO_THRESHOLD_BANNER
  );
  const isSdkSharedUser = useCheckIfSdkSharedUser();

  useUrlTriggerReset({
    paramName: 'permissionsOverride',
    trigger: REQUEST_PUSH_PERMISSIONS,
  });

  const shouldLoadContinueWatchingBanner = useShowContinueWatchingBanner();
  const isLoadingBannerFlag = useSelector(state => {
    const flagData = state.feature_flags[ROLLOUT_CONTINUE_WATCHING];

    if (!flagData) {
      return false;
    }

    return flagData.fetching && !flagData.fetched;
  });

  const hideBanners = shouldLoadContinueWatchingBanner || isLoadingBannerFlag;

  // Prioritize experiment over normal banner priority behavior
  // Show within 10 videos of limit
  if (
    hasStorageIncentive &&
    !isMobile &&
    memberVideoLimit.remainingVideos <= 10 &&
    memberVideoLimit.remainingVideos !== 0 && // Avoid showing both the p1 and p0 banner simultaneously
    canShowApproachingLimitBanner !== false // Want this to show up even when undefined
  ) {
    return (
      <ApproachingLimitBanner
        memberVideoLimit={memberVideoLimit}
        handleClose={() => {
          setShowApproachingLimitBanner(false);

          analytics.track(APPROACHING_CONTENT_LIMIT_BANNER_DISMISSED);
        }}
      />
    );
  }

  // To avoid banner fatigue we do not show these banners while some other
  // banners are being displayed
  if (hideBanners) {
    return null;
  }

  if (showMemberVideoThresholdBanner && memberVideoLimit.nearLimit) {
    return <MemberVideoThresholdBanner memberVideoLimit={memberVideoLimit} />;
  }

  const showWebPermissions =
    isLoggedIn &&
    isVideoOwner &&
    shouldRequestPushPermissions &&
    hasRecordedFirstVideo &&
    !isSdkSharedUser &&
    browserSupport &&
    Notification.permission !== PERMISSION_GRANTED;

  if (showWebPermissions) {
    return <WebPermissionsBanner />;
  }

  const showMobileBanner =
    isLoggedIn &&
    shouldShowMobileBanner &&
    hasRecordedFirstVideo &&
    browserSupport &&
    Notification.permission === PERMISSION_GRANTED;

  if (showMobileBanner) {
    return <MobileDownloadBanner onSharePage />;
  }

  if (shouldShowDataRetentionBanner) {
    return <SharePageDataRetentionBanner video={video} />;
  }

  return null;
};

export default Banners;
